import { COLORS, STATUS } from '@btime/constants'
import { capitalizeAllIfLower, fromNow } from '@btime/helpers'
import { useTranslation } from 'react-i18next'
import React from 'react'

import { RefetchingIndicator } from '../../../../components/helpers/StatePresenter'
import { compliancesClient } from '../../../../utils/apollo/api'
import { genNonComplianceColoringRuleColumn } from './_helpers/nonComplianceColoringColumn'
import { getQueryParam } from '../../../../utils/helpers/url'
import { useActionPlansCountColoringColumn } from './_helpers/actionPlansCountColoringColumn'
import { useNonComplianceActionsList } from '../../../../utils/actions/nonCompliance/list'
import { useNonCompliances } from '../_load/NonCompliancesLoader'
import { useOpenNonComplianceModal } from '../../../../utils/routes/modals/modalsActions'
import { useRowActionsColumn } from '../../../../components/tables/Table'
import DataTable from '../../../../packages/kiti/components/table/DataTable'
import Link from '../../../../components/actions/Link'
import ListCounter from '../../../../components/helpers/ListCounter'
import NonComplianceStatusTag from '../../_shared/NonComplianceStatusTag'
import PlacePresenter from '../../../../containers/places/PlacePresenter'
import Text, { SmallText } from '../../../../packages/kiti/components/presentation/Text'

const NonComplianceTable = () => {
  const { t } = useTranslation('listNonCompliance')

  const data = useNonCompliances()
  const openNonComplianceModal = useOpenNonComplianceModal()
  const origin = getQueryParam('origin') || 'serviceOrder'

  const columns = [
    {
      label: t('question'),
      headerSideContent: (
        <>
          <ListCounter
            client={compliancesClient}
            entity="nonCompliances"
            filterName="NonComplianceFilter"
            filters={{ origins: origin }}
          />
          <RefetchingIndicator />
        </>
      ),
      dataIndex: ['question', 'name'],
      sortIndex: 'QUESTION_NAME',
      flex: 3,
      render: (value, record) => (
        <Link onClick={() => openNonComplianceModal(record)} block>
          <Text size="xxsmall" bold color={COLORS.dark3} breakWord>
            {capitalizeAllIfLower(record.question?.category?.name)}
          </Text>

          <Text bold color={COLORS.dark2} breakWord>
            {capitalizeAllIfLower(value)}
          </Text>

          <SmallText size="xxsmall" color={COLORS.dark3} breakWord>
            {capitalizeAllIfLower(record.question?.checklist?.name)}
          </SmallText>
        </Link>
      )
    },

    {
      label: t('place'),
      dataIndex: 'place',
      sortIndex: 'PLACE_NAME',
      render: (item, record) => <PlacePresenter place={item} size="small" color={COLORS.dark3} hideIcon breakWord />,
      flex: 3
    },

    {
      label: t('status'),
      dataIndex: 'status',
      sortIndex: 'STATUS',
      flex: 1.5,
      render: (value, record) => <NonComplianceStatusTag name={value} />
    },

    {
      label: t('opened'),
      dataIndex: 'insertedAt',
      sortIndex: 'INSERTED_AT',
      render: (value, record) => genNonComplianceColoringRuleColumn(fromNow)(value, record.status !== STATUS.OPEN),
      flex: 1.5
    },

    {
      label: t('concluded'),
      dataIndex: 'endDate',
      sortIndex: 'END_DATE',
      format: fromNow,
      flex: 1.5
    },

    {
      label: t('instancesCount'),
      dataIndex: 'instancesCount',
      sortIndex: false,
      render: (count, record) => (
        <Link onClick={() => openNonComplianceModal(record)} block>
          <SmallText size="small" color={COLORS.dark3} breakWord>
            {t('instances', { count })}
          </SmallText>

          <Text size="xxsmall" color={COLORS.dark3} breakWord>
            {t('lastTime', { time: fromNow(record.updatedAt) })}
          </Text>
        </Link>
      ),
      flex: 2
    },

    {
      label: t('actionPlans'),
      dataIndex: 'actionPlansCount',
      sortIndex: false,
      render: useActionPlansCountColoringColumn,
      width: 6
    },

    {
      label: t('common:actions'),
      width: 3,
      sortIndex: false,
      render: useRowActionsColumn(useNonComplianceActionsList)
    }
  ]

  return <DataTable rowKey="id" columns={columns} size="medium" data={data} inPanel />
}

export default NonComplianceTable
